import React, { useEffect, useState, useMemo } from "react";
import { ConfigService } from "../../services/ConfigService";
import { usePermissions } from "../../PermissionsProvider";
import { ModelConfig } from "../../model_gen/config";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "../ui/select";
import { Label } from "../ui/label";

interface ModelSelectProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  companyId?: number;
  showEmptyOption?: boolean;
  intentGroupId?: number;
  modelType?: "all" | "server" | "client";
}

const ModelSelect: React.FC<ModelSelectProps> = ({
  value,
  onChange,
  placeholder = "Select a model",
  label = "Model",
  disabled = false,
  companyId = -1,
  showEmptyOption = true,
  intentGroupId,
  modelType = "all",
}) => {
  const { apiKey } = usePermissions();
  const [models, setModels] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setIsLoading(true);
    ConfigService.getModels(
      intentGroupId ?? companyId,
      setIsLoading,
      apiKey
    ).subscribe(
      (modelData: ModelConfig) => {
        let modelsToDisplay: string[] = [];

        if (modelType === "server" && modelData.server_models) {
          modelsToDisplay = modelData.server_models;
        } else if (modelType === "client" && modelData.client_models) {
          modelsToDisplay = modelData.client_models;
        } else {
          modelsToDisplay = modelData.all_models || [];
        }

        setModels(modelsToDisplay);
        setIsLoading(false);
      },
      (error: any) => {
        console.error("Error fetching models:", error);
        setIsLoading(false);
      }
    );
  }, [apiKey, companyId, intentGroupId, modelType]);

  // Helper functions from ConfigurationPanel
  const groupModelsByProvider = (models: string[]) => {
    const grouped: { [provider: string]: string[] } = {};
    models.forEach((model) => {
      const [provider] = model.split(":");
      if (!grouped[provider]) {
        grouped[provider] = [];
      }
      if (!grouped[provider].includes(model)) {
        grouped[provider].push(model);
      }
    });
    return grouped;
  };

  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Group and sort models - memoized to avoid recalculations on every render
  const { groupedModels, sortedProviders } = useMemo(() => {
    const grouped = groupModelsByProvider(models);
    const sorted = Object.keys(grouped).sort((a, b) => {
      if (a.toLowerCase() === "maitai") return -1;
      if (b.toLowerCase() === "maitai") return 1;
      return a.localeCompare(b);
    });
    return { groupedModels: grouped, sortedProviders: sorted };
  }, [models]);

  return (
    <div className="space-y-2">
      {label && <Label className="text-sm font-gc-med text-foreground">{label}</Label>}
      <Select 
        value={value === "" ? "none" : (value || "none")} 
        onValueChange={(newValue) => {
          // Convert "none" back to empty string for the parent component
          const actualValue = newValue === "none" ? "" : newValue;
          onChange?.(actualValue);
        }}
        disabled={disabled || isLoading}
      >
        <SelectTrigger>
          <SelectValue placeholder={isLoading ? "Loading..." : placeholder} />
        </SelectTrigger>
        <SelectContent>
          {showEmptyOption && (
            <SelectItem value="none">None</SelectItem>
          )}
          {sortedProviders.map((provider) => (
            <SelectGroup key={provider}>
              <SelectLabel className={provider.toLowerCase() === "maitai" ? "text-maitai-green font-gc-med" : ""}>
                {provider.toLowerCase() === "maitai" 
                  ? `${capitalizeFirstLetter(provider)} (Recommended)`
                  : capitalizeFirstLetter(provider)
                }
              </SelectLabel>
              {groupedModels[provider].map((model) => {
                const [, ...modelParts] = model.split(":");
                const modelName = modelParts.join(":");
                return (
                  <SelectItem key={model} value={model}>
                    {modelName}
                  </SelectItem>
                );
              })}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default ModelSelect; 